import { clear } from 'console';

import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useForm, useFormState, Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { PatternFormat, NumericFormat, NumberFormatBase, usePatternFormat } from 'react-number-format';
import MaskedInput from 'react-text-mask';
import { z } from 'zod';

import LoadingSvg from '@/components/common/LoadingSvg';
import { Button } from '@/components/ui/button';

import { useGiftcard } from '../hooks/useGiftcard';

interface GiftCardProps {
    name?: string;
}
type FormInput = z.infer<typeof FormSchema>;
const FormSchema = z.object({
    giftcardnumber: z.string().min(23, { message: 'Please enter a valid gift card number (19 numbers)' }),
    giftcardpin: z.string().min(4, { message: 'Please enter a valid PIN (4 numbers)' }).max(4),
});

export const FormGiftCard = ({ name }: GiftCardProps) => {
    const isMutating = useIsMutating();
    const {
        handleSubmit,
        register,
        control,
        setError,
        watch,
        clearErrors,
        getFieldState,
        formState: { errors, isDirty, isValid, dirtyFields, touchedFields, isSubmitting },
    } = useForm<FormInput>({
        resolver: zodResolver(FormSchema),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        criteriaMode: 'all',
        defaultValues: {
            giftcardnumber: '',
            giftcardpin: '',
        },
    });

    const [balance, setBalance] = useState(null);
    const [formattedValue, setFormattedValue] = useState('');

    const mutation = useGiftcard();

    const giftcardnumberWatch = watch('giftcardnumber', '');
    const giftcardpinWatch = watch('giftcardpin', '');
    const giftCardPageUrl =
        window.tscConfig && window.tscConfig.GiftCardPageUrl ? window.tscConfig.GiftCardPageUrl : '';

    const onSubmit = (data) => {
        const payload = {
            CardNumber: data.giftcardnumber.replace(/\s/g, ''),
            PIN: data.giftcardpin,
        };
        mutation.mutate(payload, {
            onSuccess: (data) => {
                setBalance(data.data);
            },
            onError: (error) => {
                setBalance(null);
                if (error && typeof error === 'string') {
                    setError('root.serverError', {
                        type: error as string,
                        message: error,
                    });
                }
            },
        });
    };

    useEffect(() => {
        if (errors.root) {
            clearErrors('root');
        }
    }, [giftcardnumberWatch]);

    return (
        <div className="giftcard">
            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-4">
                    {/* --------- Error --------- */}
                    {errors.root && (
                        <div className="giftcard__error mb-4">
                            <img
                                src="/App_Assets/public/icon-exclamation-black.svg"
                                alt="error-icon"
                                className="error-img"
                            ></img>
                            {errors.root.serverError.message}
                        </div>
                    )}

                    {/* --------- Giftcardnumber --------- */}
                    <div className="giftcard__form-wrap">
                        <div className="tscform__form-wrap">
                            <Controller
                                name="giftcardnumber"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Number is required' }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <InputMask
                                            alwaysShowMask={true}
                                            mask={'9999 9999 9999 9999 999'}
                                            data-empty={giftcardnumberWatch === '' ? 'true' : 'false'}
                                            data-error={getFieldState('giftcardnumber').error ? 'true' : 'false'}
                                            maskPlaceholder={''}
                                            onBlur={field.onBlur}
                                            // onChange={(event) => {
                                            //     debugger;
                                            //     field.onChange(event);
                                            //     if (errors.giftcardnumber || errors.root) {
                                            //         clearErrors();
                                            //     }
                                            // }}
                                            {...field}
                                            type="tel"
                                            className="tscform__form-input rounded-none text-base"
                                            tabIndex={0}
                                            data-qm-block="true"
                                        />

                                        <label className="tscform__form-label" htmlFor="giftcardnumber">
                                            Gift card number
                                        </label>
                                        {fieldState.error && (
                                            <div className="tscform__error-wrap">{fieldState.error.message}</div>
                                        )}
                                    </>
                                )}
                            />
                            {/* below is two attempts to get a newer input mask library to work
                        in the end i could not get it to work properly.
                        the one directly below was close but pasting a number in lost the last digit */}
                            {/* <Controller
                            name="giftcardnumber"
                            control={control}
                            defaultValue="6006 4"
                            rules={{ required: 'Number is required' }}
                            render={({ field, fieldState }) => (
                                <>
                                    <PatternFormat
                                        format="6006 4### #### #### ###"
                                        valueIsNumericString
                                        // defaultValue={'6006 2'}
                                        allowEmptyFormatting
                                        data-empty={'false'}
                                        data-error={getFieldState('giftcardnumber').error ? 'true' : 'false'}
                                        className="tscform__form-input text-base"
                                        onBlur={field.onBlur}
                                        // onChange={(event) => {
                                        //     field.onChange(event);
                                        // }}
                                        onPaste={handlePaste}
                                        name={name}
                                        value={formattedValue}
                                        onValueChange={(values) => {
                                            //setFormattedValue(values.formattedValue);
                                            //field.onChange(values.value); //works
                                            field.onChange(values.value);
                                            if (errors.giftcardnumber) {
                                                clearErrors('giftcardnumber');
                                            }
                                        }}
                                        tabIndex={0}
                                        type="tel"
                                    />

                                    <label className="tscform__form-label" htmlFor="giftcardnumber">
                                        Gift card number
                                    </label>
                                    {fieldState.error && (
                                        <div className="tscform__error-wrap">{fieldState.error.message}</div>
                                    )}
                                </>
                            )}
                        /> */}
                            {/* <PatternFormat
                            type="tel"
                            allowEmptyFormatting
                            format="6006 4### #### #### ###"
                            //defaultValue={'6006 4'}
                            value={formattedValue}
                            onValueChange={(values) => setFormattedValue(values.formattedValue)}
                            // onValueChange={(values) => {
                            //     // Mirror the value for form validation
                            //     //setFormValue('amount', values.value);
                            //     console.log(values.formattedValue);
                            // }}
                            customInput={(props) => {
                                return (
                                    <>
                                        {console.log(props)}
                                        <input
                                            name="giftcardnumber"
                                            className="tscform__form-input text-base"
                                            data-empty={'false'}
                                            data-error={getFieldState('giftcardpin').error ? 'true' : 'false'}
                                            {...props}
                                        />
                                        <label className="tscform__form-label" htmlFor="giftcardnumber">
                                            Gift card number
                                        </label>
                                        {errors.giftcardnumber && (
                                            <div className="tscform__error-wrap">{errors.giftcardnumber.message}</div>
                                        )}
                                    </>
                                );
                            }}
                        /> */}
                        </div>
                    </div>

                    {/* --------- Pin --------- */}

                    <Controller
                        name="giftcardpin"
                        control={control}
                        rules={{ required: 'Number is required' }}
                        render={({ field, fieldState }) => (
                            <>
                                <div className="tscform__form-wrap">
                                    <MaskedInput
                                        mask={[/\d/, /\d/, /\d/, /\d/]}
                                        {...field}
                                        data-empty={giftcardpinWatch === '' ? 'true' : 'false'}
                                        data-error={getFieldState('giftcardpin').error ? 'true' : 'false'}
                                        className="tscform__form-input rounded-none text-base"
                                        guide={false}
                                        onChange={(event) => {
                                            field.onChange(event);
                                            if (errors.giftcardpin) {
                                                clearErrors('giftcardpin');
                                            }
                                        }}
                                        tabIndex={0}
                                        type="tel"
                                        data-qm-block="true"
                                    />
                                    <label className="tscform__form-label" htmlFor="giftcardpin">
                                        PIN #
                                    </label>
                                    {fieldState.error && (
                                        <div className="tscform__error-wrap">{fieldState.error.message}</div>
                                    )}
                                </div>
                            </>
                        )}
                    />
                </div>

                {/* --------- Submit --------- */}
                <div>
                    {/* giftcard__buttons--container */}
                    <div className="mb-5 mt-4 grid grid-cols-1">
                        <Button
                            type="submit"
                            aria-label={'Check balance'}
                            // className="giftcard__buttons save"
                            disabled={!isValid || isSubmitting}
                            tabIndex={!isValid ? -1 : 0}
                        >
                            Check balance
                        </Button>
                    </div>
                </div>

                {/* --------- Link --------- */}
                {giftCardPageUrl && (
                    <a href={giftCardPageUrl} target="_blank" className="giftcard__link" rel="noreferrer">
                        Buy a gift card
                    </a>
                )}

                {/* --------- Balance --------- */}
                {balance && (
                    <div className="giftcard__balance">
                        <img src="/App_Assets/public/balance.svg" alt="balance-icon" className="balance-img"></img>
                        Your gift card has a remaining balance of {balance}
                    </div>
                )}
            </form>
            {mutation.isLoading || isMutating ? (
                <div className="blockPageWrap" style={{ display: 'block' }}>
                    <div className="blockPageOverlay"></div>
                    <div className="blockMsg">
                        <LoadingSvg />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
